//@ts-check
import { InputStream } from '../lmvtk/common/InputStream.js';
import { calculateLevelsToProcess } from './RcsLodUtils.js';

/** Verbose logging for debugging. */
const VERBOSE = false;

/**
 * Reads a 64-bit unsigned integer from two consecutive 32-bit little-endian words.
 * The result is a JavaScript number, which can represent integers up to 2^53 safely.
 * @param {InputStream} stream
 * @returns {number}
 */
function readUint64(stream) {
  const low = stream.getUint32();
  const high = stream.getUint32();
  return low + high * 0x100000000;
}

/**
 * @typedef {import('./RcsExtractorCore').RcsHeader} RcsHeader
 * @typedef {import('./RcsExtractorCore').RcsChunk} RcsChunk
 * @typedef {import('./RcsExtractorCore').UmbrellaLeafNode} UmbrellaLeafNode
 * @typedef {import('./RcsFileLoader').OctreeNode} OctreeNode
 * @typedef {import('./RcsFileLoader').RcsOctreeStructure} RcsOctreeStructure
 */

// ── Type Definitions ─────────────────────────────────────────────────

/**
 * A single umbrella intermediate (interior) node from chunk 3.
 * @typedef {Object} UmbrellaIntermediateNode
 * @property {number}   index            - Index within the intermediate node array
 * @property {number[]} rgba             - [r, g, b, a] vertex colour (0–255)
 * @property {number}   normalIndex      - Compressed normal index (14 bits)
 * @property {number}   childInfo        - Bitmask of occupied octree children (8 bits)
 * @property {number[]} svoBounds        - Axis-aligned bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]} misc             - 4 flag bytes [m0, m1, m2, m3]
 * @property {number}   indexIntoLeafArray - Index into the umbrella leaf array (−1 for interior nodes)
 * @property {number}   firstChildIndex  - Offset to first child within the next level's node range (−1 for leaves)
 */

/**
 * Extended umbrella data parsed from chunk 3.
 * @typedef {Object} UmbrellaExtendedData
 * @property {number[]}                   levelNodeCounts       - Number of intermediate nodes per octree level (length 32)
 * @property {UmbrellaIntermediateNode[]} intermediateNodes      - All intermediate nodes across all levels
 * @property {number[]}                   nodeLevelStartIndices  - Cumulative start indices per level (length 33)
 */

/**
 * A decoded SVO interior node.
 * @typedef {Object} SvoInteriorNode
 * @property {number}  indexToFirstChild - Offset to first child in the next level's node range (19 bits)
 * @property {number}  childMask        - Bitmask of occupied children (8 bits)
 * @property {boolean} isLeaf           - True if this node stores a leaf point (no children)
 * @property {number}  offset           - Byte offset in the SVO payload
 * @property {number}  level            - Octree level of this node
 */

/**
 * A fully decoded SVO leaf point (used by the verbose/diagnostic path).
 * @typedef {Object} SvoLeafPoint
 * @property {number}  x - Quantised X coordinate (18 bits, dequantise with 0.001 scale)
 * @property {number}  y - Quantised Y coordinate
 * @property {number}  z - Quantised Z coordinate
 * @property {number}  r - Red channel (0–255)
 * @property {number}  g - Green channel (0–255)
 * @property {number}  b - Blue channel (0–255)
 * @property {number}  a - Alpha channel (0–255)
 * @property {number}  lidarClass     - LiDAR classification code (8 bits)
 * @property {number}  normalIndex    - Compressed normal index (14 bits)
 * @property {number}  primitiveClass - Primitive classification (4 bits)
 * @property {number}  deleteFlag     - 1 if the point is soft-deleted
 */

/**
 * Result of {@link RcsParser.parseSvoPayload}.
 * @typedef {Object} SvoPayloadResult
 * @property {SvoInteriorNode[]} interiorNodes  - Parsed interior nodes (empty when parseInnerNodes is false)
 * @property {number}            numInteriorNodes - Total interior node count in this SVO
 * @property {number}            numSvoLeafNodeCount - Total SVO leaf node count in this SVO
 * @property {SvoLeafPoint[]}    leafPoints       - Decoded leaf points
 * @property {UmbrellaLeafNode}  leafNode         - The umbrella leaf metadata that was parsed
 * @property {{ totalLevels: number,
 *              processedLevels: number[],
 *              totalPointsInProcessedLevels: number,
 *              processedLeafNodes: number }} levelInfo - Processing statistics
 */

/**
 * Transform data describing the SVO-local → world translation.
 * @typedef {Object} SvoTransformData
 * @property {number}  offsetX - X component of the SVO bounds origin
 * @property {number}  offsetY - Y component of the SVO bounds origin
 * @property {number}  offsetZ - Z component of the SVO bounds origin
 * @property {boolean} [isIdentityRotation] - Always true for RCS (no rotation)
 */

/**
 * Result of {@link RcsParser.decodeSvoPointsDirect}.
 * @typedef {Object} SvoDecodeResult
 * @property {Float32Array}     interleavedData - Interleaved XYZRGB (6 floats per point)
 * @property {number}           count           - Number of decoded points
 * @property {SvoTransformData} transformData   - SVO → world translation
 * @property {number[]}         bounds          - SVO bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}         pointsPerLevel  - Surviving (non-deleted) point count per octree level, indexed by level
 * @property {UmbrellaLeafNode} [leafNode]      - Echoed back when count is 0
 */

/**
 * Per-level point data entry returned by {@link RcsParser.decodeSvoPointsByLevel}.
 * @typedef {Object} SvoLevelEntry
 * @property {Float32Array} interleavedData - Interleaved XYZRGB for this level
 * @property {number}       count           - Number of points at this level
 * @property {number}       level           - Octree level index
 */

/**
 * Result of {@link RcsParser.decodeSvoPointsByLevel}.
 * @typedef {Object} SvoByLevelResult
 * @property {SvoLevelEntry[]}       pointsByLevel - One entry per octree level that contained points
 * @property {SvoTransformData|null} transformData - SVO → world translation
 * @property {number[]}              [bounds]      - SVO bounding box
 */

/**
 * Node and leaf index ranges for a set of octree levels.
 * @typedef {Object} SvoNodeRanges
 * @property {number} nodeStart - First interior-node index to process
 * @property {number} nodeEnd   - One-past-last interior-node index
 * @property {number} leafStart - First leaf-node index to process
 * @property {number} leafEnd   - One-past-last leaf-node index
 * @property {number} [minLevel] - Lowest octree level in the range
 * @property {number} [maxLevel] - Highest octree level in the range
 */

/**
 * Result of the internal level-calculation helper.
 * @typedef {Object} LevelProcessingInfo
 * @property {number[]}      levelsToProcess  - Octree levels that fall within the requested point range
 * @property {number}        lastIncludedLevel - Highest level that still contributes points
 * @property {number}        estimatedPoints   - Estimated total point count
 * @property {SvoNodeRanges} nodeRanges        - Interior-node and leaf-node index ranges
 */

/**
 * Result of {@link RcsParser.convertSvoPointsToLocal}.
 * @typedef {Object} SvoLocalCoordinatesResult
 * @property {Float32Array}    interleavedData - Interleaved XYZRGB in SVO-local space
 * @property {SvoTransformData} transformData  - SVO → world translation
 * @property {number}          count           - Number of points
 * @property {number[]}        bounds          - SVO bounding box
 */


// ── Standalone helpers ───────────────────────────────────────────────

/**
 * Extract occupied child indices from an 8-bit child-info bitmask.
 * @param {number} childInfo - Bitmask (bits 0–7 correspond to octree children 0–7)
 * @returns {number[]} Sorted list of occupied child indices
 */
export function getChildrenFromMask(childInfo) {
  const children = [];
  for (let i = 0; i < 8; i++) {
    if (childInfo & 1 << i) {
      children.push(i);
    }
  }
  return children;
}

/**
 * RCS Parser — stateless helpers for decoding all binary structures
 * within an RCS point-cloud file.
 */
export class RcsParser {

  /**
   * Parse the 98 605-byte RCS file header.
   * @param {Uint8Array} headerData - Raw header bytes
   * @returns {RcsHeader}
   */
  static parseHeader(headerData) {
    const stream = new InputStream(headerData);

    // Magic word (8 bytes)
    const magic = stream.getString(8).replace(/\0/g, '');

    // Version (8 bytes: 2 x uint32)
    const majorVersion = stream.getUint32();
    const minorVersion = stream.getUint32();

    // Translation, rotation, scale (each 24 bytes: 3 x double)
    const translation = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];
    const rotation = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];
    const scale = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // Scanner origin (24 bytes: 3 x double)
    const scannerOrigin = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // SVO bounds (48 bytes: RCBox = 6 x double)
    const svoBounds = [
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // Scan bounds (48 bytes: RCBox = 6 x double)
    const scanBounds = [
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];


    // Octree flags (4 bytes)
    const octreeFlags = stream.getUint32();
    // Provider (4 bytes)
    const provider = stream.getUint32();

    // Total points (8 bytes: uint64 read as two uint32s)
    const totalPoints = readUint64(stream);

    // Boolean flags (3 bytes)
    const hasRGB = stream.getUint8() !== 0;
    const hasNormals = stream.getUint8() !== 0;
    const hasIntensity = stream.getUint8() !== 0;

    // Scan ID (38 bytes)
    const scanId = stream.getString(38).replace(/\0/g, '');
    // Coordinate system (32 bytes)
    const coordinateSystem = stream.getString(32).replace(/\0/g, '');
    // Number of file chunks (4 bytes)
    const numFileChunks = stream.getUint32();

    return {
      magic, majorVersion, minorVersion,
      translation, rotation, scale, scannerOrigin,
      svoBounds, scanBounds,
      octreeFlags, provider, totalPoints,
      hasRGB, hasNormals, hasIntensity,
      scanId, coordinateSystem, numFileChunks,
      chunkDirectoryOffset: stream.offset
    };
  }

  /**
   * Parse the chunk directory from the RCS header buffer.
   * @param {Uint8Array} headerData - Buffer containing the file header and chunk directory data
   * @param {number} directoryOffset - Byte offset within `headerData`
   * @param {number} numChunks - Number of directory entries
   * @returns {RcsChunk[]}
   */
  static parseChunkDirectory(headerData, directoryOffset, numChunks) {
    const stream = new InputStream(headerData);
    stream.seek(directoryOffset);

    /** @type {RcsChunk[]} */
    const chunks = [];
    for (let i = 0; i < numChunks; i++) {
      const chunkId = stream.getUint32();
      stream.skip(4); // Skip padding

      const fileOffsetStart = readUint64(stream);
      const sizeInBytes = readUint64(stream);

      chunks.push({
        chunkId,
        fileOffsetStart,
        sizeInBytes,
        name: RcsParser.getChunkName(chunkId)
      });
    }

    return chunks;
  }

  /**
   * Parse umbrella octree leaf data (chunk 2).
   * @param {Uint8Array} chunkData - Raw chunk bytes
   * @returns {UmbrellaLeafNode[]}
   */
  static parseUmbrellaOctreeData(chunkData) {
    const stream = new InputStream(chunkData);

    // Read numLeafs (8 bytes: uint64)
    const numLeafs = readUint64(stream);

    console.log(`Reading ${numLeafs} umbrella leaf nodes...`);
    console.log(`Buffer size: ${chunkData.length}, Expected size: ${8 + numLeafs * 368}`);

    /** @type {UmbrellaLeafNode[]} */
    const leafNodes = [];

    for (let i = 0; i < numLeafs; i++) {
      if (stream.offset + 368 > chunkData.length) {
        console.log(`Buffer overflow at leaf ${i}: offset ${stream.offset}, buffer size ${chunkData.length}`);
        break;
      }

      // m_svoBounds (RCBox - 48 bytes: 6 x double)
      const svoBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

      // m_nodeBounds (RCBox - 48 bytes: 6 x double)
      const nodeBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];


      // m_maxDepth (4 bytes: int32)
      const maxDepth = stream.getInt32();
      // m_amountOfPoints (4 bytes: int32)
      const amountOfPoints = stream.getInt32();
      // m_fileSize (4 bytes: int32)
      const fileSize = stream.getInt32();

      // m_numNonLeafNodes[32] (128 bytes: 32 x int32)
      /** @type {number[]} */
      const numNonLeafNodes = [];
      for (let j = 0; j < 32; j++) numNonLeafNodes.push(stream.getInt32());

      // m_numLeafNodes[32] (128 bytes: 32 x int32) — SVO leaf point counts per level
      /** @type {number[]} */
      const numSvoLeafNodes = [];
      for (let j = 0; j < 32; j++) numSvoLeafNodes.push(stream.getInt32());

      // Skip padding dummy[4] (4 bytes)
      stream.skip(4);

      leafNodes.push({
        index: i,
        svoBounds, nodeBounds,
        maxDepth, amountOfPoints, fileSize,
        numNonLeafNodes, numSvoLeafNodes
      });
    }

    return leafNodes;
  }

  /**
   * Parse umbrella extended data (chunk 3).
   * @param {Uint8Array} chunkData - Raw chunk bytes
   * @returns {UmbrellaExtendedData}
   */
  static parseUmbrellaExtendedData(chunkData) {
    const stream = new InputStream(chunkData);

    // Read levelNodeCnt[32] (128 bytes: 32 x int32)
    /** @type {number[]} */
    const levelNodeCounts = [];
    for (let i = 0; i < 32; i++) levelNodeCounts.push(stream.getInt32());

    // Calculate total number of intermediate nodes
    const totalNodes = levelNodeCounts.reduce((sum, count) => sum + count, 0);
    const nodeLevelStartIndices = [0];
    for (let i = 0; i < levelNodeCounts.length; i++) {
      nodeLevelStartIndices.push(nodeLevelStartIndices[i] + levelNodeCounts[i]);
    }

    console.log(`Reading ${totalNodes} umbrella intermediate nodes...`);

    /** @type {UmbrellaIntermediateNode[]} */
    const intermediateNodes = [];

    for (let i = 0; i < totalNodes; i++) {
      // m_rgba (4 bytes: RCVector4ub)
      const rgba = [stream.getUint8(), stream.getUint8(), stream.getUint8(), stream.getUint8()];
      // m_normalIndex (2 bytes: uint16)
      const normalIndex = stream.getUint16();
      // m_childInfo (2 bytes: uint16)
      const childInfo = stream.getUint16();

      // m_svoBounds (48 bytes: RCBox = 6 x double)
      const svoBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

      // m_misc[4] (4 bytes: uint8 flags)
      const misc = [stream.getUint8(), stream.getUint8(), stream.getUint8(), stream.getUint8()];
      // m_indexIntoLeafArray (4 bytes: int32)
      const indexIntoLeafArray = stream.getInt32();
      // m_firstChildIndex (4 bytes: int32)
      const firstChildIndex = stream.getInt32();
      // Skip padding dummy[4] (4 bytes)
      stream.skip(4);

      intermediateNodes.push({
        index: i, rgba, normalIndex, childInfo,
        svoBounds, misc, indexIntoLeafArray, firstChildIndex
      });
    }

    return { levelNodeCounts, intermediateNodes, nodeLevelStartIndices };
  }

  /**
   * Parse an SVO payload buffer into interior nodes and leaf points.
   * This is the full-fidelity diagnostic decoder that preserves every field.
   *
   * @param {Uint8Array} buffer - SVO payload data
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {boolean} [isVerbose=false] - Enable console logging
   * @param {number} [startPoint=0] - Start of the point range (level index or cumulative point index)
   * @param {number} [endPoint=Infinity] - End of the point range
   * @param {boolean} [parseInnerNodes=false] - Whether to decode interior node bitfields
   * @returns {SvoPayloadResult}
   */
  static parseSvoPayload(buffer, leafNode, isVerbose = false, startPoint = 0, endPoint = Infinity, parseInnerNodes = false) {
    const stream = new InputStream(buffer);
    /** @type {SvoInteriorNode[]} */
    const interiorNodes = [];

    if (isVerbose) console.log(`\nParsing SVO payload...`);

    const levelInfo = this._calculateLevelsToProcess(leafNode, startPoint, endPoint);

    if (levelInfo.levelsToProcess.length === 0) {
      return {
        leafPoints: [],
        interiorNodes: [],
        numInteriorNodes: 0,
        numSvoLeafNodeCount: 0,
        leafNode,
        levelInfo: { totalLevels: 0, processedLevels: [], totalPointsInProcessedLevels: 0, processedLeafNodes: 0 }
      };
    }

    // Count interior nodes and leaf nodes based on umbrella data
    const numInteriorNodes = leafNode.numNonLeafNodes.reduce((sum, count) => sum + count, 0);
    const numSvoLeafNodeTotal = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    let leafCount = 0;

    const nodeLevelStartIndices = [0];
    for (let i = 0; i < leafNode.numNonLeafNodes.length; i++) {
      nodeLevelStartIndices.push(nodeLevelStartIndices[i] + leafNode.numNonLeafNodes[i]);
    }

    // Returns the largest L such that nodeLevelStartIndices[L] <= nodeIndex.
    // Binary search over the 33-entry prefix-sum table;
    /** @type {(nodeIndex: number) => number} */
    const levelOf = (nodeIndex) => {
      let lo = 0;
      let hi = nodeLevelStartIndices.length - 1;
      while (lo < hi) {
        const mid = lo + hi + 1 >> 1;
        if (nodeLevelStartIndices[mid] <= nodeIndex) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      return lo;
    };

    // Process nodes efficiently using calculated ranges
    const { nodeStart, nodeEnd } = levelInfo.nodeRanges;

    if (parseInnerNodes) {
      // Skip nodes before our target range
      stream.skip(nodeStart * 4); // Skip 4 bytes per node

      // Process only the nodes in our target range
      for (let i = nodeStart; i < nodeEnd && i < numInteriorNodes + numSvoLeafNodeTotal; i++) {
        const nodeLevel = levelOf(i);
        const nodeData = stream.getUint32();

        // Decode VoxelInteriorNode bitfield
        const indexToFirstChild = nodeData & 0x7FFFF; // bits 0-18
        const childMask = nodeData >> 19 & 0xFF; // bits 19-26
        const isLeaf = nodeData >> 27 & 0x1; // bit 27

        if (!isLeaf) {
          console.assert(childMask !== 0);
        } else {
          console.assert(childMask === 0);
        }

        interiorNodes.push({
          indexToFirstChild,
          childMask,
          isLeaf: Boolean(isLeaf),
          offset: stream.offset - 4,
          level: nodeLevel
        });

        if (isLeaf) leafCount++;
      }

      // Skip remaining nodes that we don't need
      const remainingNodes = numInteriorNodes + numSvoLeafNodeTotal - nodeEnd;
      stream.skip(remainingNodes * 4); // Skip 4 bytes per node
    } else {
      stream.skip((numInteriorNodes + numSvoLeafNodeTotal) * 4);
    }

    if (isVerbose) console.log(`Found ${interiorNodes.length} interior nodes`);
    if (isVerbose) console.log(`Found ${leafCount} leaf nodes`);
    if (isVerbose) console.log(`Leaf data starts at offset: ${stream.offset}`);

    // Parse leaf nodes efficiently using calculated ranges
    /** @type {SvoLeafPoint[]} */
    const leafPoints = [];
    const { leafStart, leafEnd } = levelInfo.nodeRanges;
    let processedLeafNodes = 0;

    // Skip leaf nodes before our target range
    stream.skip(leafStart * 16); // Skip 16 bytes per leaf node

    // Process only leaf nodes in our target range
    for (let i = leafStart; i < leafEnd && i < numSvoLeafNodeTotal; i++) {
      // Read two 64-bit words as 8 bytes each
      const word0Bytes = stream.getTypedArray(Uint8Array, 8);
      const word1Bytes = stream.getTypedArray(Uint8Array, 8);

      // Convert to 64-bit values (little-endian)
      let word0 = 0n;
      let word1 = 0n;
      for (let j = 7; j >= 0; j--) {
        word0 = word0 << 8n | BigInt(word0Bytes[j]);
        word1 = word1 << 8n | BigInt(word1Bytes[j]);
      }

      // Decode word0: x/y/z quantized offsets (18+18+18 bits), lidarClass (8), reserved (1), deleteFlag (1)
      const x = Number(word0 & 0x3FFFFn); // 18 bits
      const y = Number(word0 >> 18n & 0x3FFFFn); // 18 bits
      const z = Number(word0 >> 36n & 0x3FFFFn); // 18 bits
      const lidarClass = Number(word0 >> 54n & 0xFFn); // 8 bits
      const deleteFlag = Number(word0 >> 63n & 0x1n); // 1 bit

      // Skip deleted points
      if (deleteFlag) continue;

      // Decode word1: rgba (32), normalIndex (14), primitiveClass (4), reserved (14)
      const rgba = Number(word1 & 0xFFFFFFFFn); // 32 bits
      const normalIndex = Number(word1 >> 32n & 0x3FFFn); // 14 bits
      const primitiveClass = Number(word1 >> 46n & 0xFn); // 4 bits

      // Extract RGBA components (corrected order)
      const a = rgba & 0xFF;
      const b = rgba >> 8 & 0xFF;
      const g = rgba >> 16 & 0xFF;
      const r = rgba >> 24 & 0xFF;

      leafPoints.push({
        x, y, z, r, g, b, a,
        lidarClass, normalIndex, primitiveClass, deleteFlag
      });

      processedLeafNodes++;
    }

    // Skip remaining leaf nodes we don't need
    const remainingLeafNodes = numSvoLeafNodeTotal - leafEnd;
    stream.skip(remainingLeafNodes * 16); // Skip 16 bytes per leaf node

    if (isVerbose) console.log(`Parsed ${leafPoints.length} leaf points`);

    return {
      interiorNodes,
      numInteriorNodes,
      numSvoLeafNodeCount: numSvoLeafNodeTotal,
      leafPoints,
      leafNode,
      levelInfo: {
        totalLevels: leafNode.numSvoLeafNodes.length,
        processedLevels: levelInfo.levelsToProcess,
        totalPointsInProcessedLevels: leafPoints.length,
        processedLeafNodes
      }
    };
  }

  /**
   * Decode SVO points split by octree level for hierarchical LOD distribution.
   *
   * @param {Uint8Array} buffer - Raw SVO payload buffer
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} [startPoint=0] - Start of the point range
   * @param {number} [endPoint=Infinity] - End of the point range
   * @param {boolean} [interiorNodesIncluded] - Whether `buffer` starts with interior nodes
   * @returns {SvoByLevelResult}
   */
  static decodeSvoPointsByLevel(buffer, leafNode, startPoint = 0, endPoint = Infinity, interiorNodesIncluded) {
    const result = this.decodeSvoPointsDirect(buffer, leafNode, startPoint, endPoint, interiorNodesIncluded);

    if (!result || !result.interleavedData || result.count === 0) {
      return { pointsByLevel: [], transformData: result ? result.transformData : null };
    }

    /** @type {SvoLevelEntry[]} */
    const pointsByLevel = [];
    const totalInterleaved = result.interleavedData;
    let currentPointIndex = 0;

    // Split the packed survivor buffer back into levels. Note that there
    // are less pointsPerLevel as the raw leafNode.numSvoLeafNodes count suggests
    // as points marked with a deleteFlag are excluded.
    const pointsPerLevel = result.pointsPerLevel;
    for (let level = 0; level < pointsPerLevel.length; level++) {
      const pointsInLevel = pointsPerLevel[level];
      if (pointsInLevel <= 0) continue;

      const sourceStart = currentPointIndex * 6;
      // slice() returns an independent Float32Array, safe to transfer to a worker.
      const levelData = totalInterleaved.slice(sourceStart, sourceStart + pointsInLevel * 6);

      pointsByLevel.push({
        interleavedData: levelData,
        count: pointsInLevel,
        level
      });

      currentPointIndex += pointsInLevel;
    }

    const bounds = leafNode.svoBounds;
    /** @type {SvoTransformData} */
    const transformData = {
      offsetX: bounds[0],
      offsetY: bounds[1],
      offsetZ: bounds[2]
    };

    return { pointsByLevel, transformData, bounds: result.bounds };
  }

  /**
   * High-performance SVO decoder that writes directly into an interleaved
   * Float32Array without intermediate objects or BigInt operations.
   *
   * @param {Uint8Array} buffer - Raw SVO payload buffer
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} [startPoint=0] - Start of the point range
   * @param {number} [endPoint=Infinity] - End of the point range
   * @param {boolean} [interiorNodesIncluded=true] - Whether `buffer` starts with interior-node data
   * @returns {SvoDecodeResult}
   */
  static decodeSvoPointsDirect(buffer, leafNode, startPoint = 0, endPoint = Infinity, interiorNodesIncluded = true) {
    // Calculate which levels to process
    const levelInfo = this._calculateLevelsToProcess(leafNode, startPoint, endPoint);

    // If no levels to process, return empty result
    if (levelInfo.levelsToProcess.length === 0) {
      return {
        interleavedData: new Float32Array(0),
        count: 0,
        transformData: { offsetX: 0, offsetY: 0, offsetZ: 0 },
        bounds: leafNode.svoBounds,
        pointsPerLevel: [],
        leafNode
      };
    }

    // Count nodes
    const numInteriorNodes = leafNode.numNonLeafNodes.reduce((sum, count) => sum + count, 0);
    const numSvoLeafNodeTotal = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);

    // Calculate data offsets
    const nodeDataSize = (numInteriorNodes + numSvoLeafNodeTotal) * 4; // 4 bytes per node
    const leafDataStart = interiorNodesIncluded ? nodeDataSize : 0;

    const { leafStart, leafEnd } = levelInfo.nodeRanges;
    const leafsToProcess = leafEnd - leafStart;

    // Pre-allocate output buffer (6 floats per point: XYZ + RGB)
    // We'll process all leafs but skip deleted ones, so allocate max size
    const interleavedData = new Float32Array(leafsToProcess * 6);

    // Fixed scale factors
    const scaleX = 0.001;
    const scaleY = 0.001;
    const scaleZ = 0.001;
    const colorScale = 1.0 / 255.0;

    // DataView for efficient binary reading without BigInt
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);

    let outputIndex = 0;
    const startOffset = leafDataStart + leafStart * 16; // 16 bytes per leaf

    // Overall point count per octree level
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;
    // Surviving (non-deleted) point count per octree level
    const pointsPerLevel = new Array(numSvoLeafNodes.length).fill(0);

    let i = 0;
    for (const level of levelInfo.levelsToProcess) {
      // Add all non-deleted points in this level
      for (let n = 0; n < numSvoLeafNodes[level]; n++, i++) {
        const offset = startOffset + i * 16;

        // Read two 64-bit words as four 32-bit integers (little-endian)
        const w0_low = view.getUint32(offset, true);
        const w0_high = view.getUint32(offset + 4, true);
        const w1_low = view.getUint32(offset + 8, true);
        // w1_high not needed - only rgba in w1_low is used

        // Decode word0: x/y/z quantized offsets (18+18+18 bits), lidarClass (8), reserved (1), deleteFlag (1)
        const x = w0_low & 0x3FFFF; // Bits 0-17: x (18 bits)
        const y = (w0_low >>> 18 | (w0_high & 0xF) << 14) & 0x3FFFF; // Bits 18-35: y (18 bits)
        const z = w0_high >>> 4 & 0x3FFFF; // Bits 36-53: z (18 bits)
        const deleteFlag = w0_high >>> 31 & 0x1; // Bit 63: deleteFlag (1 bit)

        // Skip deleted points
        if (deleteFlag) continue;

        // Decode word1: rgba (32 bits), normalIndex (14), primitiveClass (4), reserved (14)
        const rgba = w1_low;
        // Extract RGBA components (order: ABGR in memory)
        const b = rgba >>> 8 & 0xFF;
        const g = rgba >>> 16 & 0xFF;
        const r = rgba >>> 24 & 0xFF;

        // Write directly to output buffer
        interleavedData[outputIndex++] = x * scaleX; // X
        interleavedData[outputIndex++] = y * scaleY; // Y
        interleavedData[outputIndex++] = z * scaleZ; // Z
        interleavedData[outputIndex++] = r * colorScale; // R
        interleavedData[outputIndex++] = g * colorScale; // G
        interleavedData[outputIndex++] = b * colorScale; // B

        pointsPerLevel[level]++;
      }
    }

    // Trim buffer to actual size (removing deleted points)
    const actualPointCount = outputIndex / 6;
    const trimmedData = actualPointCount < leafsToProcess ?
    new Float32Array(interleavedData.buffer, 0, outputIndex) :
    interleavedData;

    const bounds = leafNode.svoBounds;
    /** @type {SvoTransformData} */
    const transformData = {
      offsetX: bounds[0],
      offsetY: bounds[1],
      offsetZ: bounds[2],
      isIdentityRotation: true
    };

    return {
      interleavedData: trimmedData,
      count: actualPointCount,
      transformData,
      bounds,
      pointsPerLevel
    };
  }

  /**
   * Convert parsed SVO leaf points to SVO-local coordinates with an
   * interleaved Float32Array.
   *
   * @param {SvoPayloadResult} svoData - Output from {@link parseSvoPayload}
   * @returns {SvoLocalCoordinatesResult}
   */
  static convertSvoPointsToLocal(svoData) {
    const { leafPoints, leafNode } = svoData;
    const bounds = leafNode.svoBounds; // Use SVO bounds for transform

    // Calculate scale factors from quantized to local coordinates
    const scaleX = 0.001;
    const scaleY = 0.001;
    const scaleZ = 0.001;

    // Create interleaved buffer: XYZRGBXYZRGB...
    const interleavedData = new Float32Array(leafPoints.length * 6);

    for (let i = 0; i < leafPoints.length; i++) {
      const point = leafPoints[i];
      const baseIndex = i * 6;

      // Local coordinates (not world-transformed yet)
      interleavedData[baseIndex] = point.x * scaleX;
      interleavedData[baseIndex + 1] = point.y * scaleY;
      interleavedData[baseIndex + 2] = point.z * scaleZ;
      // Colors in 0-1 range
      interleavedData[baseIndex + 3] = point.r / 255.0;
      interleavedData[baseIndex + 4] = point.g / 255.0;
      interleavedData[baseIndex + 5] = point.b / 255.0;
    }

    // Create transform data for LMV integration
    // Return SVO origin offsets (offsetX/Y/Z) for Matrix4 in RcsFileLoader
    /** @type {SvoTransformData} */
    const transformData = {
      offsetX: bounds[0],
      offsetY: bounds[1],
      offsetZ: bounds[2],
      isIdentityRotation: true
    };

    if (VERBOSE) {
      console.log(`Converted ${leafPoints.length} points to local coordinates with interleaved buffer`);
      console.log(`SVO bounds: [${bounds.join(', ')}]`);
      console.log(`Scale factors: [${scaleX}, ${scaleY}, ${scaleZ}]`);
      console.log(`Transform data:`, transformData);
    }

    return {
      interleavedData,
      transformData,
      count: leafPoints.length,
      bounds
    };
  }

  /**
   * Build the hierarchical octree structure from umbrella leaf data and
   * umbrella extended data (intermediate nodes).
   *
   * @param {UmbrellaLeafNode[]} leafNodes - Parsed umbrella leaf nodes (chunk 2)
   * @param {UmbrellaExtendedData} extendedData - Parsed umbrella extended data (chunk 3)
   * @returns {RcsOctreeStructure}
   */
  static buildOctreeStructure(leafNodes, extendedData) {
    const { intermediateNodes, nodeLevelStartIndices } = extendedData;
    /** @type {OctreeNode[]} */
    const nodes = [];
    let nextNodeId = 0;
    /** @type {Set<number>} */
    const visitedNodes = new Set();

    function buildTreeIteratively() {
      if (intermediateNodes.length === 0) return;

      /** @type {Array<{nodeIndexInFile: number, nodeId: number, parentId: number|null, nodeLevel: number}>} */
      const queue = [{ nodeIndexInFile: 0, nodeId: nextNodeId++, parentId: null, nodeLevel: 0 }];

      let queueHead = 0;
      while (queueHead < queue.length) {
        const { nodeIndexInFile, nodeId, nodeLevel } = queue[queueHead++];

        if (nodeIndexInFile < 0 || nodeIndexInFile >= intermediateNodes.length) continue;
        if (visitedNodes.has(nodeIndexInFile)) {
          console.warn(`Circular reference detected at intermediate node ${nodeIndexInFile}`);
          continue;
        }
        visitedNodes.add(nodeIndexInFile);

        const intermediate = intermediateNodes[nodeIndexInFile];

        let indexIntoLeafArray = intermediate.indexIntoLeafArray;
        if (indexIntoLeafArray < 0 || indexIntoLeafArray >= leafNodes.length) {
          indexIntoLeafArray = -1;
        }

        const leafNode = indexIntoLeafArray !== -1 ? leafNodes[indexIntoLeafArray] : null;

        /** @type {OctreeNode} */
        let node;
        if (leafNode) {
          console.assert(intermediate.firstChildIndex === -1);
          // Leaf node: owns SVO data and has no children.
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children: [],
            isLeaf: true,
            fileOffsets: {
              svoDataSize: leafNode.fileSize,
              leafIndex: indexIntoLeafArray
            },
            numSvoLeafNodes: leafNode.numSvoLeafNodes,
            numNonLeafNodes: leafNode.numNonLeafNodes,
            fragmentId: indexIntoLeafArray, // Direct mapping
            nodeBounds: leafNode.nodeBounds
          };
        } else {
          // Interior node: queue its children. A node that references a leaf
          // index but has no leaf data also lands here, as a childless interior
          // node (matching the previous behaviour).
          const children = [];
          if (indexIntoLeafArray === -1) {
            const childMask = getChildrenFromMask(intermediate.childInfo);
            if (childMask.length > 0 && intermediate.firstChildIndex >= 0) {
              for (let i = 0; i < childMask.length; i++) {
                const childIndex = nodeLevelStartIndices[nodeLevel + 1] + intermediate.firstChildIndex + i;
                if (childIndex >= 0 && childIndex < intermediateNodes.length) {
                  const childNodeId = nextNodeId++;
                  children.push(childNodeId);
                  queue.push({
                    nodeIndexInFile: childIndex,
                    nodeId: childNodeId,
                    parentId: nodeId,
                    nodeLevel: nodeLevel + 1
                  });
                }
              }
            }
          }
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children,
            isLeaf: false,
            fileOffsets: {}
          };
        }

        nodes.push(node);
      }
    }

    // Note: after the build nodes is sorted in ascending order or element.id, root is first.
    buildTreeIteratively();

    console.log(`Built octree with ${nodes.length} nodes, visited ${visitedNodes.size} intermediate nodes`);

    return { nodes };
  }

  /**
   * Map a numeric chunk ID to a human-readable name.
   * @param {number} chunkId
   * @returns {string}
   */
  static getChunkName(chunkId) {
    /** @type {Record<number, string>} */
    const chunkNames = {
      1: 'Voxel data (SVO interior + leaf points)',
      2: 'Umbrella octree data (leaf summaries)',
      3: 'Umbrella extended (hierarchy details)',
      4: 'Range image (PNG)',
      5: 'Preview image (JPEG/PNG)',
      6: 'Segment info',
      7: 'Voxel segment data',
      8: 'Segment image (PNG)',
      9: 'RGB image data (JPEG)',
      10: 'E57 properties',
      11: 'Other info (global metadata)',
      12: 'Occlusion info',
      13: 'Time stamps'
    };
    return chunkNames[chunkId] || `Unknown chunk ${chunkId}`;
  }

  /**
   * Decode octree flag bits into human-readable names.
   * @param {number} flags
   * @returns {string[]}
   */
  static getOctreeFlags(flags) {
    const flagNames = [];
    if (flags & 0x01) flagNames.push('has color');
    if (flags & 0x02) flagNames.push('has intensity');
    if (flags & 0x04) flagNames.push('has normals');
    if (flags & 0x08) flagNames.push('has LiDAR classification');
    if (flags & 0x10) flagNames.push('has primitive classification');
    if (flags & 0x20) flagNames.push('has range image');
    if (flags & 0x40) flagNames.push('compressed with zlib');
    if (flags & 0x80) flagNames.push('uses compressed voxel format');
    if (flags & 0x100) flagNames.push('has time stamps');
    return flagNames;
  }

  /**
   * Calculate the interior-node and leaf-node index ranges for a requested
   * point range by mapping the range to octree levels.
   *
   * @private
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} startPoint - Start of the point range
   * @param {number} endPoint - End of the point range
   * @returns {LevelProcessingInfo}
   */
  static _calculateLevelsToProcess(leafNode, startPoint, endPoint) {
    const numNonLeafNodes = leafNode.numNonLeafNodes;
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;

    // Use shared utility to calculate levels to process
    const { levelsToProcess, totalPoints } =
    calculateLevelsToProcess(numSvoLeafNodes, startPoint, endPoint);

    // Determine last included level
    const lastIncludedLevel = levelsToProcess.length > 0 ?
    levelsToProcess[levelsToProcess.length - 1] :
    -1;

    // Calculate node ranges for efficient processing
    const nodeRanges = this._calculateNodeRanges(numNonLeafNodes, numSvoLeafNodes, levelsToProcess);

    return {
      levelsToProcess,
      lastIncludedLevel,
      estimatedPoints: totalPoints,
      nodeRanges
    };
  }

  /**
   * Compute start/end indices for interior nodes and leaf nodes given a
   * set of octree levels.
   *
   * @private
   * @param {number[]} numNonLeafNodes - Interior node counts per level
   * @param {number[]} numSvoLeafNodes - SVO leaf point counts per level
   * @param {number[]} levelsToProcess - Which levels to include
   * @returns {SvoNodeRanges}
   */
  static _calculateNodeRanges(numNonLeafNodes, numSvoLeafNodes, levelsToProcess) {
    if (levelsToProcess.length === 0) {
      return { nodeStart: 0, nodeEnd: 0, leafStart: 0, leafEnd: 0 };
    }

    const minLevel = Math.min(...levelsToProcess);
    const maxLevel = Math.max(...levelsToProcess);

    // Calculate interior node ranges
    let nodeStart = 0;
    let nodeEnd = 0;
    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) nodeStart += numNonLeafNodes[level] || 0;
      nodeEnd += numNonLeafNodes[level] || 0;
    }

    // Calculate leaf node ranges
    let leafStart = 0;
    let leafEnd = 0;
    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) leafStart += numSvoLeafNodes[level] || 0;
      if (level <= maxLevel) leafEnd += numSvoLeafNodes[level] || 0;
    }

    return { nodeStart, nodeEnd, leafStart, leafEnd, minLevel, maxLevel };
  }
}